// ============================================================
// MOONSHINE MASTERS - SOUND (2026-10-30, ROADMAP 3) - PLACEHOLDER SOUNDS, made in code
// The distilling research's best cues: the Thumper's thump-thump while a run goes through it, and the hiss of a
// clogged line. Plus a pop, a BOOM, a soft blip for news, and a pick-up chime. Every sound is synthesized with
// WebAudio (no files, nothing hosted, no drain). Real recorded sounds can replace any of them later: one function each.
// The browser only allows sound after a click / key, so App calls unlock() when you start the game.
// Muting is per player, remembered in this browser (localStorage `moonshine_sound`).
// ============================================================
export const SOUND = { VOL: 0.32, THUMP_EVERY_S: 0.95, HISS_VOL: 0.06 };   // dials

let ctx = null, master = null, hissNode = null, thumpTimer = null, wantHiss = false;   // wantHiss: a clog that began before the audio was running still hisses once it is
let muted = false;
try { muted = localStorage.getItem("moonshine_sound") === "off"; } catch (e) { /* private mode */ }
const subs = new Set();

export const isMuted = () => muted;
export function setMuted(m) {
  muted = !!m;
  try { localStorage.setItem("moonshine_sound", muted ? "off" : "on"); } catch (e) { /* private mode */ }
  if (master) master.gain.value = muted ? 0 : SOUND.VOL;
  subs.forEach((f) => f(muted));
}
export const onMuted = (f) => { subs.add(f); return () => subs.delete(f); };

export function unlock() {
  try {
    if (!ctx) {
      const AC = window.AudioContext || window.webkitAudioContext;
      if (!AC) return;
      ctx = new AC();
      master = ctx.createGain(); master.gain.value = muted ? 0 : SOUND.VOL; master.connect(ctx.destination);
    }
    if (ctx.state === "suspended") ctx.resume().then(() => { if (wantHiss) hiss(true); }).catch(() => {});
  } catch (e) { ctx = null; }
}
const ready = () => !!(ctx && master && ctx.state === "running");

// one short tone that slides from f0 to f1 over `len` seconds
function tone(f0, f1, len, type = "sine", vol = 1, at = 0) {
  if (!ready()) return;
  const t = ctx.currentTime + at, o = ctx.createOscillator(), g = ctx.createGain();
  o.type = type; o.frequency.setValueAtTime(f0, t); o.frequency.exponentialRampToValueAtTime(Math.max(20, f1), t + len);
  g.gain.setValueAtTime(0.0001, t); g.gain.exponentialRampToValueAtTime(vol, t + 0.01); g.gain.exponentialRampToValueAtTime(0.0001, t + len);
  o.connect(g); g.connect(master); o.start(t); o.stop(t + len + 0.05);
}
// a burst of filtered noise
function noise(len, freq, q = 1, vol = 1, type = "bandpass") {
  if (!ready()) return null;
  const n = Math.floor(ctx.sampleRate * len), buf = ctx.createBuffer(1, n, ctx.sampleRate), d = buf.getChannelData(0);
  for (let i = 0; i < n; i++) d[i] = Math.random() * 2 - 1;
  const src = ctx.createBufferSource(), f = ctx.createBiquadFilter(), g = ctx.createGain(), t = ctx.currentTime;
  src.buffer = buf; f.type = type; f.frequency.value = freq; f.Q.value = q;
  g.gain.setValueAtTime(vol, t); g.gain.exponentialRampToValueAtTime(0.0001, t + len);
  src.connect(f); f.connect(g); g.connect(master); src.start(t);
  return src;
}

// ── the one-shots ──
const SFX = {
  thump: () => { tone(95, 42, 0.22, "sine", 0.9); tone(70, 38, 0.18, "triangle", 0.5, 0.26); },   // thump-thump
  pop: () => { noise(0.25, 1800, 0.8, 0.9); tone(420, 120, 0.12, "square", 0.25); },
  boom: () => { noise(1.4, 140, 0.5, 1.2, "lowpass"); tone(80, 28, 1.1, "sine", 1); },
  blip: () => tone(660, 880, 0.12, "sine", 0.25),
  gather: () => { tone(740, 990, 0.1, "triangle", 0.3); tone(990, 1320, 0.12, "triangle", 0.22, 0.08); },
  wisp: () => { tone(880, 1760, 0.4, "sine", 0.25); tone(1320, 2200, 0.5, "sine", 0.15, 0.1); },
  caught: () => { tone(220, 70, 0.5, "sawtooth", 0.35); noise(0.3, 400, 0.7, 0.5); },
  coin: () => { tone(1046, 1046, 0.08, "square", 0.15); tone(1568, 1568, 0.16, "square", 0.12, 0.07); },
  // 2026-10-31 STARS + REVENUERS: the fuzz is close - a two-tone wail, ~1.2 s (up-down, twice)
  siren: () => { for (let i = 0; i < 4; i++) { const up = i % 2 === 0; tone(up ? 620 : 960, up ? 960 : 620, 0.3, "triangle", 0.4, i * 0.3); tone(up ? 620 : 960, up ? 960 : 620, 0.3, "square", 0.06, i * 0.3); } },
};
export function sfx(name) { if (!muted && SFX[name]) try { SFX[name](); } catch (e) { /* audio off */ } }

// ── the loops: the clog hiss, and the thumper while a run is going ──
export function hiss(on) {
  wantHiss = !!on;
  try {
    if (on && !hissNode && ready()) {
      const n = ctx.sampleRate * 2, buf = ctx.createBuffer(1, n, ctx.sampleRate), d = buf.getChannelData(0);
      for (let i = 0; i < n; i++) d[i] = Math.random() * 2 - 1;
      const src = ctx.createBufferSource(), f = ctx.createBiquadFilter(), g = ctx.createGain();
      src.buffer = buf; src.loop = true; f.type = "highpass"; f.frequency.value = 3200; g.gain.value = SOUND.HISS_VOL;
      src.connect(f); f.connect(g); g.connect(master); src.start();
      hissNode = src;
    } else if (!on && hissNode) { hissNode.stop(); hissNode = null; }
  } catch (e) { hissNode = null; }
}
export function thumping(on) {
  if (on && !thumpTimer) { sfx("thump"); thumpTimer = setInterval(() => sfx("thump"), SOUND.THUMP_EVERY_S * 1000); }
  else if (!on && thumpTimer) { clearInterval(thumpTimer); thumpTimer = null; }
}
export function quiet() { hiss(false); thumping(false); }
